import { useRef, useEffect, useMemo, DependencyList, EffectCallback } from 'react';
import isEqual from 'react-fast-compare';

/**
 * Custom hook that compares two values and returns a boolean indicating if they have updated.
 *
 * @param firstValuesToCompare - The first value to compare.
 * @param SecondValuesToCompare - The second value to compare.
 * @returns A boolean indicating if the values have updated.
 */
export const useHasUpdated = <T>(firstValuesToCompare: T, SecondValuesToCompare: T): boolean =>
  useMemo(() => !isEqual(firstValuesToCompare, SecondValuesToCompare), [firstValuesToCompare, SecondValuesToCompare]);

/**
 * Custom hook that runs a callback function only on component mount.
 *
 * @param callback - The callback function to run on mount.
 * @param deps - The dependency array for the useEffect hook.
 */
export const useDidMount = (callback: () => void, deps: unknown[] = []): void => {
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender?.current) {
      firstRender.current = false;
      return;
    }
    callback?.();
    // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  }, deps);
};

/**
 * Custom hook that performs a deep comparison with the previous value and returns a boolean indicating if it has changed.
 *
 * @param value - The value to compare.
 * @returns A tuple containing a boolean indicating if the value has changed and the previous value.
 */
export const useDeepCompareWithPrevious = <T>(value?: T): [boolean, T | undefined] => {
  const prevVal = usePrevious(value);
  return [!isEqual(prevVal, value), prevVal];
};

/**
 * Custom hook that performs a deep comparison with the previous value and returns a boolean indicating if it has changed.
 *
 * @param value - The value to compare.
 * @returns A boolean indicating if the value has changed.
 */
export const useDeepCompare = <T>(value: T): boolean => {
  const prevVal = usePrevious(value);
  return !isEqual(prevVal, value);
};

/**
 * Custom hook that compares the current value with the previous value and returns a boolean indicating if they are different.
 *
 * @param value - The value to compare.
 * @returns A boolean indicating if the value has changed.
 */
export const useCompare = <T>(value: T): boolean => {
  const prevVal = usePrevious(value);
  return prevVal !== value;
};

/**
 * Custom hook that stores the previous value of a given state or prop.
 *
 * @param value - The value to store.
 * @returns The previous value.
 */
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

/**
 * Custom hook that runs an effect with deep comparison of dependencies.
 *
 * @param effect - The effect callback function to run.
 * @param depsToDeepCompare - The dependency list to compare deeply.
 * @param otherDeps - Optional additional dependencies to include in the effect.
 */
export const useDeepCompareEffect = (
  effect: EffectCallback,
  depsToDeepCompare: DependencyList,
  otherDeps?: DependencyList
) => {
  const hasChanged = useDeepCompare(depsToDeepCompare);
  const deps = [...(otherDeps ?? EMPTY_DEP_LIST)];

  // Use effect with deep comparison of dependencies
  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore effect to avoid infinite loop
  useEffect(() => {
    let e: ReturnType<EffectCallback>;
    if (hasChanged) {
      e = effect();
    }
    return () => {
      e?.();
    };
  }, [hasChanged, ...deps]);
};

const EMPTY_DEP_LIST = [] as DependencyList;
